import React from "react";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import CloseIcon from "@mui/icons-material/Close";

import Menu from "@mui/material/Menu";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import {
  FIELD_TYPES,
  isManualType,
  hasOptions,
  isScaleLikeType,
  scaleBounds,
} from "./formFieldHelpers";
import MediaLibraryModal from "./MediaLibraryModal";
import { uploadLearningFile } from "./swipeDeckHelpers";
import {
  opsForType,
  SHOW_IF_OP_LABELS,
  canBeReferenced,
  canBePiped,
  sourceNameMap,
  singleLine,
  toDisplayLabel,
  fromDisplayLabel,
  splitAnswerTokens,
  isKnownSourceName,
  validateShowIf,
  defaultShowIfValue,
  danglingPipingRefs,
} from "./formLogic";
import ReadOnlyField from "./FormFieldReadOnly";

const selectOutlineSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#d1d5db" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
};

/**
 * One field's card in the center "whole form" stack. The answer control renders
 * the way the completer will see it — the shared `ReadOnlyField` (grayed,
 * non-interactive, matches the Preview modal) — with the question + helper
 * inline-editable above it and the editing chrome (type dropdown, required,
 * scoring) revealed only on the focused card. So an unfocused card reads like
 * the live form; clicking it surfaces the controls. single_select keeps
 * inline-editable options instead of a static preview.
 */
export function FormFieldEditorPane({
  field,
  index,
  isSelected,
  scoringEnabled,
  completedByRole,
  onUpdate, // (patch)
  onUpdateOption, // (optIdx, patch)
  onAddOption, // ()
  onRemoveOption, // (optIdx)
  autoFocus, // focus the question/heading input (set when newly added)
  earlierFields = [], // fields before this one (show_if targets / piping)
  companyId, // for uploading a field's image
  isEnding = false, // a trailing info screen: the player shows it after submit
}) {
  // Grab focus on the title when this card is flagged (a freshly added field),
  // without scrolling — the parent handles smooth scroll-into-view separately.
  const titleRef = React.useRef(null);
  React.useEffect(() => {
    if (autoFocus && titleRef.current) {
      titleRef.current.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  // Last caret position in the question input. `onSelect` fires on click,
  // arrow keys, and drag-select, so this survives the input losing focus to the
  // "Insert answer" menu — which is where the token has to land.
  //
  // The text it was measured against is kept with it: a token's displayed
  // length depends on the wording of the question it points at, so renaming or
  // reordering that question shifts every offset after it. An offset from the
  // old text could then splice into the middle of a token and turn a live
  // reference into literal text.
  const caretRef = React.useRef(null);
  const rememberCaret = (e) => {
    caretRef.current = {
      display: e.target.value,
      start: e.target.selectionStart,
      end: e.target.selectionEnd,
    };
  };

  // Drop an {{answer: …}} token at the caret (mirrors the swipe-card emoji
  // picker), leaving the cursor just past it so typing continues naturally.
  // Both the caret and the token are in DISPLAY space — what the input holds —
  // and the whole string converts back to ids on the way out. With no caret yet
  // (the menu opened without the label ever being clicked into) it appends
  // rather than silently prefixing at index 0.
  const insertPipingToken = (id) => {
    // The UNIQUE name, not the question's raw wording: with two questions
    // worded the same, the bare name would convert back to the first of them.
    const token = `{{answer: ${sourceNameMap(earlierFields).get(id)}}}`;
    // Collapsed exactly as the input collapses it: the caret is an offset into
    // what the author sees, and comparing it against an uncollapsed string
    // would reject a perfectly good caret (and splice into different text).
    const display = singleLine(toDisplayLabel(field.label || "", earlierFields));
    // Only trust the caret if the text hasn't moved under it since.
    const caret =
      caretRef.current && caretRef.current.display === display ? caretRef.current : null;
    const [start, end] = caret ? [caret.start, caret.end] : [display.length, display.length];
    const spacer = !caret && display ? " " : "";
    const next =
      display.slice(0, start) + spacer + token + display.slice(end);
    onUpdate({ label: fromDisplayLabel(next, earlierFields) });
    const pos = start + spacer.length + token.length;
    caretRef.current = { display: next, start: pos, end: pos };
    requestAnimationFrame(() => {
      const input = titleRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      input.setSelectionRange(pos, pos);
    });
  };

  if (!field) return null;

  const canScore = (type) =>
    type === "scale" ||
    type === "single_select" ||
    (isManualType(type) && completedByRole === "employee");

  const cardClass = [
    "rounded-xl border bg-white p-5 transition-colors",
    // Match the swipe deck's selected card accent (violet-600).
    isSelected ? "border-violet-600 ring-1 ring-violet-600" : "border-slate-200",
  ].join(" ");

  // What kind of field this is, stated rather than offered. Retyping in place
  // had to rewrite everything hanging off the old type — options, scale bounds,
  // scoring, and every later question's condition pointing at this one — for a
  // change that is rare and always expressible as "delete it and add the one
  // you meant". The chrome is shown only on the focused card.
  const typeLabel =
    FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type;
  const typeChrome = isSelected && (
    <div className="flex items-center gap-2 mb-3">
      <span className="inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
        {typeLabel}
      </span>
      <span className="text-xs text-gray-400">Field {index + 1}</span>
    </div>
  );

  const pipeTargets = earlierFields.filter((f) => canBePiped(f.type) && f.label);
  const showIfTargets = earlierFields.filter((f) => canBeReferenced(f.type));

  // Info screen: a display-only screen (headline, message, optional image) used
  // as a slide-show form's welcome / transition / closing screen.
  if (field.type === "info_screen") {
    return (
      <div className={cardClass}>
        {typeChrome}
        {isEnding && (
          <p className="mb-3 inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
            Last screen, shown after they submit
          </p>
        )}
        <FieldImageEditor
          field={field}
          companyId={companyId}
          isSelected={isSelected}
          onUpdate={onUpdate}
        />
        <div className="flex items-start gap-1">
          <PipedTextInput
            inputRef={titleRef}
            value={field.label}
            onChange={(label) => onUpdate({ label })}
            onSelect={rememberCaret}
            placeholder="Headline"
            className="flex-1 min-w-0 text-lg font-semibold text-slate-900"
            earlierFields={earlierFields}
          />
          {isSelected && pipeTargets.length > 0 && (
            <PipingMenu targets={pipeTargets} onInsert={insertPipingToken} />
          )}
        </div>
        <PipingHints field={field} earlierFields={earlierFields} />
        {(isSelected || field.help) && (
          <PipedTextInput
            value={field.help || ""}
            onChange={(help) => onUpdate({ help })}
            placeholder="Message (optional). Explain why you're asking, set up the next section, or thank them."
            className="mt-1.5 w-full text-sm text-slate-600"
            earlierFields={earlierFields}
            allowNewlines
          />
        )}
        {isSelected && (
          <ShowIfEditor field={field} targets={showIfTargets} onUpdate={onUpdate} />
        )}
      </div>
    );
  }

  // Section heading: the styled heading text + optional description (the
  // statement text in slide mode), inline-editable.
  if (field.type === "heading") {
    return (
      <div className={cardClass}>
        {typeChrome}
        <input
          ref={titleRef}
          type="text"
          value={field.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Section heading"
          className="w-full bg-transparent text-sm font-semibold uppercase tracking-wide text-slate-600 border-none focus:outline-none placeholder:normal-case placeholder:font-normal placeholder:text-slate-400 placeholder:tracking-normal"
        />
        {(isSelected || field.help) && (
          <PipedTextInput
            value={field.help || ""}
            onChange={(help) => onUpdate({ help })}
            placeholder="Description (optional), shown under the heading."
            className="mt-1 w-full text-xs text-slate-500"
            earlierFields={earlierFields}
            allowNewlines
          />
        )}
        <FieldImageEditor
          field={field}
          companyId={companyId}
          isSelected={isSelected}
          onUpdate={onUpdate}
          variant="inline"
        />
        {isSelected && (
          <ShowIfEditor
            field={field}
            targets={showIfTargets}
            onUpdate={onUpdate}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cardClass}>
      {typeChrome}

      {/* Question label — styled like the rendered question, inline-editable.
          The input fills the row (flex-1) so it never clips the text; the
          required `*` is pinned to the right edge. */}
      <div className="flex items-start gap-1">
        <PipedTextInput
          inputRef={titleRef}
          value={field.label}
          onChange={(label) => onUpdate({ label })}
          onSelect={rememberCaret}
          placeholder="Question"
          className="flex-1 min-w-0 text-base font-medium text-slate-900"
          earlierFields={earlierFields}
        />
        {field.required && (
          <span className="text-red-500 leading-none text-base mt-1">*</span>
        )}
        {isSelected && pipeTargets.length > 0 && (
          <PipingMenu targets={pipeTargets} onInsert={insertPipingToken} />
        )}
      </div>

      <PipingHints field={field} earlierFields={earlierFields} />

      {/* Helper text — subtle, editable; hidden when empty + unfocused. */}
      {(isSelected || field.help) && (
        <PipedTextInput
          value={field.help || ""}
          onChange={(help) => onUpdate({ help })}
          placeholder="Helper text (optional)"
          className="mt-1 w-full text-xs text-slate-500"
          earlierFields={earlierFields}
          allowNewlines
        />
      )}

      {/* Answer control. Unfocused (and every non-select type) shows the grayed,
          non-interactive preview via the shared ReadOnlyField — so the card reads
          like the live form. A focused single_select swaps to editable options. */}
      <div className="mt-3">
        {hasOptions(field.type) && isSelected ? (
          <SingleSelectEditor
            field={field}
            isSelected={isSelected}
            scoringEnabled={scoringEnabled && field.type === "single_select"}
            multi={field.type === "multi_select"}
            onUpdateOption={onUpdateOption}
            onAddOption={onAddOption}
            onRemoveOption={onRemoveOption}
          />
        ) : (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
              Preview
            </div>
            <ReadOnlyField field={field} controlOnly />
            {/* NPS bounds are fixed at 0-10; only the end labels are editable. */}
            {field.type === "nps" && isSelected && (
              <div className="mt-3 flex items-center gap-3">
                <input
                  type="text"
                  value={field.min_label || ""}
                  onChange={(e) => onUpdate({ min_label: e.target.value })}
                  placeholder="Low label (e.g. Not at all likely)"
                  className="w-44 px-2 py-1 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
                <input
                  type="text"
                  value={field.max_label || ""}
                  onChange={(e) => onUpdate({ max_label: e.target.value })}
                  placeholder="High label (e.g. Extremely likely)"
                  className="w-44 px-2 py-1 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
              </div>
            )}
            {field.type === "scale" && isSelected && (
              <div className="mt-3 flex items-center gap-3">
                <label className="text-xs text-gray-600">
                  Min
                  <input
                    type="number"
                    value={field.min}
                    min={0}
                    onChange={(e) => {
                      const min = Math.max(0, Number(e.target.value) || 0);
                      onUpdate({ min, max: Math.max(field.max, min) });
                    }}
                    className="ml-2 w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg"
                  />
                </label>
                <label className="text-xs text-gray-600">
                  Max
                  <input
                    type="number"
                    value={field.max}
                    onChange={(e) =>
                      onUpdate({ max: Math.max(Number(e.target.value), field.min) })
                    }
                    className="ml-2 w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg"
                  />
                </label>
                <input
                  type="text"
                  value={field.min_label || ""}
                  onChange={(e) => onUpdate({ min_label: e.target.value })}
                  placeholder="Low label (e.g. Not likely)"
                  className="w-40 px-2 py-1 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
                <input
                  type="text"
                  value={field.max_label || ""}
                  onChange={(e) => onUpdate({ max_label: e.target.value })}
                  placeholder="High label (e.g. Very likely)"
                  className="w-40 px-2 py-1 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* The question's image (e.g. the plating being judged). Sits under the
          control here so the card's question -> answer reading order holds;
          learners see it above the control, where it sets up the question. */}
      <FieldImageEditor
        field={field}
        companyId={companyId}
        isSelected={isSelected}
        onUpdate={onUpdate}
        variant="inline"
      />

      {/* Display condition — only on the focused card. */}
      {isSelected && (
        <ShowIfEditor field={field} targets={showIfTargets} onUpdate={onUpdate} />
      )}

      {/* Manual-grading config — only on the focused card. */}
      {isSelected &&
        scoringEnabled &&
        isManualType(field.type) &&
        field.scored &&
        completedByRole === "employee" && (
          <div className="mt-3 rounded-lg bg-violet-50 border border-violet-100 p-3 space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-700">
                Max points
                <input
                  type="number"
                  min={0}
                  value={field.max_points ?? 1}
                  onChange={(e) =>
                    onUpdate({ max_points: Math.max(0, Number(e.target.value) || 0) })
                  }
                  className="ml-2 w-20 px-2 py-1 text-sm border border-gray-300 rounded-lg"
                  title="Most points a grader can award for this answer"
                />
              </label>
              <span className="text-xs text-violet-700 font-medium">Graded manually</span>
            </div>
            <textarea
              rows={2}
              placeholder="Answer key / rubric (optional) — shown to the grader; used by AI grading later"
              value={field.answer_key || ""}
              onChange={(e) => onUpdate({ answer_key: e.target.value })}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700 resize-none"
            />
          </div>
        )}

      {/* Required + scoring toggles — only on the focused card. */}
      {isSelected && (
        <div className="mt-3 flex items-center gap-4 pt-3 border-t border-slate-100">
          <label className="flex items-center gap-1.5 text-xs text-gray-700">
            <input
              type="checkbox"
              checked={field.required}
              onChange={(e) => onUpdate({ required: e.target.checked })}
              className="accent-violet-600"
            />
            Required
          </label>
          {scoringEnabled && canScore(field.type) && (
            <label
              className={`flex items-center gap-1.5 text-xs ${
                field.show_if ? "text-gray-400" : "text-gray-700"
              }`}
              title={
                field.show_if
                  ? "A conditional question can't count toward the score. Remove its condition first."
                  : undefined
              }
            >
              <input
                type="checkbox"
                disabled={!!field.show_if && !field.scored}
                checked={field.scored}
                onChange={(e) =>
                  onUpdate({
                    scored: e.target.checked,
                    ...(e.target.checked &&
                    isManualType(field.type) &&
                    field.max_points == null
                      ? { max_points: 1 }
                      : {}),
                  })
                }
                className="accent-violet-600"
              />
              {isManualType(field.type) ? "Graded (manual)" : "Counts toward score"}
            </label>
          )}
        </div>
      )}
    </div>
  );
}

function PipingHints({ field, earlierFields }) {
  if (!danglingPipingRefs(field, earlierFields).length) return null;
  return (
    <p className="mt-0.5 text-xs text-red-600">
      This inserts an answer from a question that no longer comes before it.
      Remove the inserted answer or move this below its source.
    </p>
  );
}

// A field's optional image: upload a file or pick one from the company's media
// library. Stores the LearningFile uuid (durable) plus the presigned URL for the
// in-editor preview (expiring, re-resolved server-side on every read), mirroring
// the swipe-card editors.
//
// variant "block" is the info screen, where the picture is the point, so an
// empty one shows a drop area. "inline" is a question, where it's the exception
// — an empty one is just a small "+ Add image" link, so every focused question
// card isn't half dashed box.
function FieldImageEditor({ field, companyId, isSelected, onUpdate, variant = "block" }) {
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState(null);
  const [libraryOpen, setLibraryOpen] = React.useState(false);
  const imageUrl = field.image_url || null;

  const handleImagePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setUploadError("Please choose an image file.");
      return;
    }
    setUploading(true);
    setUploadError(null);
    try {
      const { uuid, url } = await uploadLearningFile(companyId, file);
      onUpdate({ image_uuid: uuid, image_url: url });
    } catch (err) {
      setUploadError(err?.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Nothing to show and nothing to do on an unfocused card without an image.
  if (!imageUrl && !isSelected) return null;

  const fileInput = (
    <input
      type="file"
      accept="image/*"
      onChange={handleImagePick}
      disabled={uploading}
      className="hidden"
    />
  );

  return (
    <div className={variant === "block" ? "mb-3" : "mt-3"}>
      {imageUrl ? (
        <div className="relative inline-block max-w-full rounded-lg overflow-hidden border border-slate-200">
          <img src={imageUrl} alt="" className="w-full max-h-40 object-cover" />
          {isSelected && (
            <button
              type="button"
              onClick={() => onUpdate({ image_uuid: null, image_url: null })}
              className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/70 text-white text-xs hover:bg-black/90"
            >
              Remove
            </button>
          )}
        </div>
      ) : variant === "block" ? (
        <label className="flex items-center justify-center h-16 rounded-lg border-2 border-dashed border-slate-300 hover:border-violet-500 cursor-pointer transition-colors text-xs text-slate-500">
          {uploading ? "Uploading…" : "Add an image (optional)"}
          {fileInput}
        </label>
      ) : (
        <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-violet-700 cursor-pointer">
          <ImageOutlinedIcon style={{ fontSize: 14 }} />
          {uploading ? "Uploading…" : "+ Add image"}
          {fileInput}
        </label>
      )}
      {/* Only an entry point for picking an image, so it disappears once there
          is one — Remove puts the card back to the empty state, where both
          "add" paths (upload, library) are one click away. It also floated
          awkwardly beside the thumbnail, since the image box shrinks to the
          picture's width. */}
      {isSelected && !imageUrl && (
        <button
          type="button"
          onClick={() => setLibraryOpen(true)}
          disabled={uploading}
          className={[
            "inline-flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:text-violet-800 disabled:opacity-50",
            variant === "block" ? "mt-1.5" : "ml-3",
          ].join(" ")}
        >
          <CollectionsOutlinedIcon style={{ fontSize: 14 }} />
          Choose from library
        </button>
      )}
      {uploadError && <div className="mt-1 text-xs text-red-600">{uploadError}</div>}
      <MediaLibraryModal
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        companyId={companyId}
        onSelect={({ uuid, url }) => onUpdate({ image_uuid: uuid, image_url: url })}
      />
    </div>
  );
}

// An inline-editable label. The input holds the AUTHORING form of the text —
// {{answer: <question>}} — while the field stores {{field:<id>}}; the two
// convert on the way in and out. One representation, focused or not: the
// earlier version swapped a readable overlay for the raw ids whenever the input
// took focus, so the same question read two different ways depending on what
// was selected.
function PipedTextInput({
  inputRef,
  value,
  onChange,
  onSelect,
  placeholder,
  className,
  earlierFields,
  allowNewlines = false,
}) {
  const ownRef = React.useRef(null);
  const ref = inputRef || ownRef;

  // A label is one line of prose — Enter would insert a newline the rendered
  // question can't show, so it's swallowed and pasted breaks collapse to
  // spaces. Helper text and info screen messages DO render line breaks
  // (whitespace-pre-wrap), so they keep them.
  const clean = (text) => (allowNewlines ? text : singleLine(text));

  // Applied on the way IN as well, not just to what's typed. A label saved
  // earlier (or drafted by AI) can hold an embedded newline — the backend
  // strips only the ends — and this editor preserves whitespace while every
  // learner-facing label collapses it. Showing it collapsed here keeps the
  // preview honest about what gets delivered; the first edit persists it.
  const display = clean(toDisplayLabel(value, earlierFields));

  // A textarea can't colour part of its own text, so the real text is made
  // transparent and this layer, sitting exactly behind it with identical
  // typography, paints it: violet for a token that names a question coming
  // before this one, plain for everything else. Editing inside a token stops it
  // matching, so it turns plain the moment it stops being a variable.
  const highlight = splitAnswerTokens(display).map((seg, i) =>
    seg.token && isKnownSourceName(seg.name, earlierFields) ? (
      <span key={i} className="text-violet-700">
        {seg.token}
      </span>
    ) : (
      <React.Fragment key={i}>{seg.token || seg.text}</React.Fragment>
    )
  );

  // The highlight layer is the one IN FLOW, so the card's height comes from
  // real wrapped text and grows with it; the textarea is stretched over it.
  // Measuring the textarea instead (scrollHeight) fixed the height at one
  // moment in time, and any later rewrap — a web font finishing loading, the
  // column changing width — left the text spilling over whatever sat below.
  // The zero-width space keeps a line box for an empty value, and preserves a
  // trailing blank line in a message.
  return (
    <div className={`relative ${className || ""}`}>
      <div aria-hidden className="whitespace-pre-wrap break-words">
        {highlight}
        {"\u200b"}
      </div>
      <textarea
        ref={ref}
        value={display}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !allowNewlines) e.preventDefault();
        }}
        onChange={(e) => onChange(fromDisplayLabel(clean(e.target.value), earlierFields))}
        onSelect={onSelect}
        placeholder={placeholder}
        // text-transparent hides the real glyphs in favour of the layer behind;
        // the caret and any selected run stay visible so editing still reads
        // normally.
        // h-full, not just inset-0: a textarea is sized like a replaced element,
        // so with height:auto it takes its intrinsic row count instead of
        // stretching — leaving the lower wrapped lines outside the editable
        // control on a long label, and overhanging the controls below on a
        // short one.
        className="absolute inset-0 block h-full w-full resize-none overflow-hidden break-words border-none bg-transparent p-0 text-transparent caret-slate-900 outline-none selection:text-slate-900 placeholder:text-slate-400 placeholder:font-normal"
      />
    </div>
  );
}

// single_select renders as option rows that look like the live control (a radio
// bullet + the option text), with the text inline-editable. Add/remove + per-
// option points appear only on the focused card.
function SingleSelectEditor({
  field,
  isSelected,
  scoringEnabled,
  multi = false,
  onUpdateOption,
  onAddOption,
  onRemoveOption,
}) {
  const options = field.options || [];
  const labels = options.map((o) => (o.label || "").trim()).filter(Boolean);
  const hasAnyLabel = labels.length > 0;
  const dupLabels = new Set(labels).size !== labels.length;

  return (
    <div className="space-y-2">
      {options.map((opt, optIdx) => (
        <div key={optIdx} className="flex items-center gap-2">
          <span
            className={`w-4 h-4 border-2 border-slate-300 shrink-0 ${
              multi ? "rounded" : "rounded-full"
            }`}
          />
          <input
            type="text"
            placeholder={`Option ${optIdx + 1}`}
            value={opt.label}
            onChange={(e) => onUpdateOption(optIdx, { label: e.target.value })}
            className="flex-1 min-w-0 bg-transparent text-sm text-slate-800 border-b border-transparent focus:border-violet-700 focus:outline-none py-1 placeholder:text-slate-400"
          />
          {isSelected && scoringEnabled && field.scored && (
            <input
              type="number"
              placeholder="pts"
              value={opt.points}
              onChange={(e) =>
                onUpdateOption(optIdx, { points: Number(e.target.value) || 0 })
              }
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
              title="Points awarded for this option"
            />
          )}
          {isSelected && options.length > 1 && (
            <button
              type="button"
              onClick={() => onRemoveOption(optIdx)}
              className="text-gray-400 hover:text-red-500 shrink-0"
              aria-label="Remove option"
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      ))}

      {isSelected && (
        <button
          type="button"
          onClick={onAddOption}
          className="ml-6 text-xs font-medium text-violet-700 hover:text-violet-800"
        >
          + Add option
        </button>
      )}

      {isSelected && !hasAnyLabel && (
        <p className="text-xs text-red-500">
          Add at least one option so learners have a choice to select.
        </p>
      )}
      {isSelected && dupLabels && (
        <p className="text-xs text-red-500">Each option needs a unique label.</p>
      )}
    </div>
  );
}


// "Show only if" rule editor: pick an earlier field, an op (depends on that
// field's type), and a value. Stored as field.show_if = {field, op, value}.
function ShowIfEditor({ field, targets, onUpdate }) {
  const rule = field.show_if || null;
  const ref = rule ? targets.find((t) => t.id === rule.field) : null;
  const ops = ref ? opsForType(ref.type) : [];
  const inputSx = {
    height: 32,
    fontSize: 12,
    borderRadius: "8px",
    ...selectOutlineSx,
  };

  const setRule = (patch) => {
    const next = { ...(rule || {}), ...patch };
    // Re-validate the op against the (possibly new) target's type.
    const target = targets.find((t) => t.id === next.field);
    const allowed = target ? opsForType(target.type) : [];
    if (!allowed.includes(next.op)) next.op = allowed[0] || "answered";
    if (next.op === "answered") delete next.value;
    else if (next.value === undefined) next.value = defaultShowIfValue(target);
    onUpdate({ show_if: next });
  };

  const problem = rule ? validateShowIf(rule, targets) : null;

  if (!targets.length && !rule) return null;
  // Scored fields can't be conditional (max reachable score would depend on
  // the branch). Offer no "Show only if" on a scored field; if both are set
  // (e.g. scoring toggled on later) say so, since Save is blocked.
  if (!rule && field.scored) return null;

  // The rule points at a field that is missing, later in the form, or a
  // heading (e.g. after a reorder or type change). Save is blocked until it's
  // fixed, so say so here rather than silently disabling Save.
  if (rule && problem === "missing_target") {
    return (
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 text-xs text-red-600">
        <span>
          This "show only if" condition points at a question that no longer comes
          before this one. Remove it or move this question below its gate.
        </span>
        <button
          type="button"
          onClick={() => onUpdate({ show_if: undefined })}
          className="shrink-0 font-semibold underline"
        >
          Remove condition
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      {!rule ? (
        <button
          type="button"
          onClick={() => setRule({ field: targets[targets.length - 1].id })}
          className="text-xs font-medium text-slate-500 hover:text-violet-700"
        >
          + Show only if…
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
          <span className="font-medium">Show only if</span>
          <Select
            value={rule.field}
            onChange={(e) => setRule({ field: e.target.value, value: undefined })}
            size="small"
            sx={{ ...inputSx, maxWidth: 220 }}
          >
            {targets.map((t) => (
              <MenuItem key={t.id} value={t.id} sx={{ fontSize: 12 }}>
                {t.label || "(untitled question)"}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={ops.includes(rule.op) ? rule.op : ops[0] || "answered"}
            onChange={(e) => setRule({ op: e.target.value })}
            size="small"
            sx={inputSx}
          >
            {ops.map((op) => (
              <MenuItem key={op} value={op} sx={{ fontSize: 12 }}>
                {SHOW_IF_OP_LABELS[op]}
              </MenuItem>
            ))}
          </Select>
          {rule.op !== "answered" && ref && (
            <ShowIfValueInput
              refField={ref}
              value={rule.value}
              onChange={(value) => setRule({ value })}
              sx={inputSx}
            />
          )}
          <button
            type="button"
            onClick={() => onUpdate({ show_if: undefined })}
            className="text-gray-400 hover:text-red-500"
            aria-label="Remove condition"
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
          {problem && (
            <span className="basis-full text-red-600">
              {problem === "bad_op"
                ? "This comparison doesn't fit that question's type. Pick another."
                : "Choose a value that still exists on that question."}
            </span>
          )}
          {!problem && field.scored && (
            <span className="basis-full text-red-600">
              A scored question can't be conditional. Remove the condition or turn
              off "Counts toward score".
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ShowIfValueInput({ refField, value, onChange, sx }) {
  const t = refField.type;
  if (t === "yes_no" || t === "checkbox") {
    return (
      <Select
        value={value === false ? "false" : "true"}
        onChange={(e) => onChange(e.target.value === "true")}
        size="small"
        sx={sx}
      >
        <MenuItem value="true" sx={{ fontSize: 12 }}>
          {t === "checkbox" ? "checked" : "Yes"}
        </MenuItem>
        <MenuItem value="false" sx={{ fontSize: 12 }}>
          {t === "checkbox" ? "unchecked" : "No"}
        </MenuItem>
      </Select>
    );
  }
  if (t === "single_select" || t === "multi_select") {
    const opts = (refField.options || []).filter((o) => o.label);
    return (
      <Select
        value={opts.some((o) => o.label === value) ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        displayEmpty
        sx={{ ...sx, maxWidth: 220 }}
      >
        <MenuItem value="" sx={{ fontSize: 12 }} disabled>
          Choose an option
        </MenuItem>
        {opts.map((o) => (
          <MenuItem key={o.label} value={o.label} sx={{ fontSize: 12 }}>
            {o.label}
          </MenuItem>
        ))}
      </Select>
    );
  }
  if (isScaleLikeType(t)) {
    const { min, max } = scaleBounds(refField);
    return (
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? min}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-lg outline-none transition-colors focus:border-violet-700"
      />
    );
  }
  return null;
}

// Small "Insert answer" menu: appends a {{field:id}} token to the question so
// an earlier answer is piped into the wording ("How long have you worked at
// {{field:home}}?").
function PipingMenu({ targets, onInsert }) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="shrink-0 text-[11px] font-medium text-slate-400 hover:text-violet-700 whitespace-nowrap"
        title="Insert an earlier answer into this question"
      >
        + Insert answer
      </button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { borderRadius: "10px", maxWidth: 360 } } }}
      >
        {targets.map((t) => (
          <MenuItem
            key={t.id}
            onClick={() => {
              onInsert(t.id);
              setAnchorEl(null);
            }}
            title={t.label}
            // Long questions wrap inside the menu instead of running off its
            // right edge (MenuItem defaults to nowrap).
            sx={{ fontSize: 13, whiteSpace: "normal", lineHeight: 1.35, py: 1 }}
          >
            {t.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
